import * as React from "react";

import {
  Button,
  Grid,
  Group,
  MantineSize,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { StyleSheet } from "@react-pdf/renderer";
import { IconApps, IconArrowNarrowLeft, IconChecklist } from "@tabler/icons-react";
import _ from "lodash";
import { useNavigate } from "react-router-dom";

import { blankTemplate } from "../../data/templates/blank/blank";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useCountEvent } from "../../hooks/useCountEvent";
import { newChecklistDocumentFromTemplates } from "../../lib/generators/checklist";
import {
  AircraftAddonTemplate,
  BlankTemplate,
  SoftwareAddonTemplate,
} from "../../lib/model/addonTemplate";
import { ChecklistContents } from "../../lib/model/checklistAppState";
import { DocumentLayout, documentLayoutNames, documentLayouts } from "../../lib/model/layout";
import { aircraftAddonTemplates, softwareAddonTemplates } from "../../lib/state/addonTemplates";
import { defaultDocumentStyles } from "../../lib/state/defaultStyles";
import { librarySlice } from "../../lib/state/reducer";
import { AddonTemplateCard } from "../addons/AddonTemplateCard";
import { SoftPDFPreview } from "../renderer/softpdf/SoftPDFPreview";

type WizardStep = "start" | "chooseAircraftTemplate" | "chooseAddonTemplates";

type AddonStep = "chooseAircraftTemplate" | "chooseAddonTemplates";

const wizardStepToModalSizeMap: Record<WizardStep, MantineSize> = {
  start: "sm",
  chooseAircraftTemplate: "xl",
  chooseAddonTemplates: "xl",
};

const wizardStepToTitleMap: Record<WizardStep, string> = {
  start: "Create a new document",
  chooseAircraftTemplate: "Choose an aircraft to start from",
  chooseAddonTemplates: "Add additional addon programs",
};

export const NewDocumentWizard = ({
  open,
  onClose,
  isEditorEmpty,
}: {
  open: boolean;
  onClose: () => void;
  isEditorEmpty: boolean;
}) => {
  const [wizardStep, setWizardStep] = React.useState<WizardStep>("start");
  const reduxDispatch = useAppDispatch();
  const eventCounter = useCountEvent();

  React.useEffect(() => {
    if (!open) {
      setWizardStep("start");
    }
  }, [setWizardStep, open]);

  const wrappedOnClose = React.useCallback(() => {
    if (isEditorEmpty) {
      reduxDispatch(librarySlice.actions.newDocument());
    }
    eventCounter("close-new-document-wizard");
    onClose();
  }, [onClose, reduxDispatch]);

  return (
    <Modal
      centered
      opened={open}
      onClose={wrappedOnClose}
      size={wizardStepToModalSizeMap[wizardStep]}
      title={<Title order={3}>{wizardStepToTitleMap[wizardStep]}</Title>}
      closeOnClickOutside={!isEditorEmpty}
    >
      <Wizard wizardStep={wizardStep} setWizardStep={setWizardStep} onClose={onClose} />
    </Modal>
  );
};

export const Wizard = ({
  wizardStep,
  setWizardStep,
  onClose,
}: {
  wizardStep: WizardStep;
  setWizardStep: (step: WizardStep) => void;
  onClose: () => void;
}): JSX.Element => {
  const doClose = React.useCallback(() => {
    onClose();
  }, [onClose]);
  switch (wizardStep) {
    case "start":
      return <WizardStartStep onClose={doClose} setWizardStep={setWizardStep} />;
    case "chooseAircraftTemplate":
    case "chooseAddonTemplates":
      return (
        <AddonTemplateWizard
          wizardStep={wizardStep}
          setWizardStep={setWizardStep}
          onClose={doClose}
        />
      );
  }
  const _exhaustiveCheck: never = wizardStep;
  return _exhaustiveCheck;
};

export const WizardStartStep = ({
  onClose,
  setWizardStep,
}: {
  onClose: () => void;
  setWizardStep: (step: WizardStep) => void;
}): JSX.Element => {
  const reduxDispatch = useAppDispatch();
  const eventCounter = useCountEvent();
  const createNewDocument = React.useCallback(() => {
    reduxDispatch(librarySlice.actions.newDocument());
    eventCounter("wizard-start-blank-document");

    onClose();
  }, [reduxDispatch, onClose]);

  return (
    <Stack>
      <Button
        variant="gradient"
        gradient={{ from: "cyan.4", to: "blue", deg: 45 }}
        size="md"
        leftIcon={<IconChecklist size={24} />}
        onClick={createNewDocument}
      >
        Start a blank document
      </Button>
      <Button
        variant="gradient"
        gradient={{ from: "orange", to: "red", deg: 45 }}
        size="md"
        leftIcon={<IconApps size={24} />}
        onClick={() => {
          setWizardStep("chooseAircraftTemplate");
          eventCounter("wizard-start-from-template");
        }}
      >
        Choose from templates
      </Button>
    </Stack>
  );
};

export const AddonTemplateWizard = ({
  wizardStep,
  setWizardStep,
  onClose,
}: {
  wizardStep: AddonStep;
  setWizardStep: (step: WizardStep) => void;
  onClose: () => void;
}): JSX.Element | null => {
  const [selectedBaseTemplate, setSelectedBaseTemplate] = React.useState<
    AircraftAddonTemplate | BlankTemplate | null
  >(null);

  switch (wizardStep) {
    case "chooseAircraftTemplate":
      return (
        <ChooseAircraftTemplateStep
          setWizardStep={setWizardStep}
          setBaseTemplate={setSelectedBaseTemplate}
        />
      );
    case "chooseAddonTemplates":
      if (selectedBaseTemplate === null) {
        return null;
      }
      return (
        <ChooseSoftwareAddonTemplatesStep
          baseTemplate={selectedBaseTemplate}
          setWizardStep={setWizardStep}
          onClose={onClose}
        />
      );
  }
  const _exhaustiveCheck: never = wizardStep;
  return _exhaustiveCheck;
};

export const ChooseAircraftTemplateStep = ({
  setWizardStep,
  setBaseTemplate,
}: {
  setWizardStep: (step: WizardStep) => void;
  setBaseTemplate: (t: AircraftAddonTemplate | BlankTemplate) => void;
}): JSX.Element => {
  const back = React.useCallback(() => {
    setWizardStep("start");
  }, [setWizardStep]);

  return (
    <Stack>
      <Grid>
        {[...aircraftAddonTemplates, blankTemplate].map((addon) => (
          <Grid.Col lg={3} sm={4} xs={6} key={addon.id}>
            <AddonTemplateCard
              addonTemplate={addon}
              primaryActionLabel="Select"
              primaryAction={() => {
                setBaseTemplate(addon);
                setWizardStep("chooseAddonTemplates");
              }}
            />
          </Grid.Col>
        ))}
      </Grid>
      <Group>
        <Button onClick={() => back()} variant="outline" leftIcon={<IconArrowNarrowLeft />}>
          Back
        </Button>
      </Group>
    </Stack>
  );
};

export const ChooseSoftwareAddonTemplatesStep = ({
  baseTemplate,
  setWizardStep,
  onClose,
}: {
  baseTemplate: AircraftAddonTemplate | BlankTemplate;
  setWizardStep: (step: WizardStep) => void;
  onClose: () => void;
}): JSX.Element => {
  const [selectedAddons, setSelectedAddons] = React.useState<SoftwareAddonTemplate[]>([]);
  const back = React.useCallback(() => {
    setWizardStep("chooseAircraftTemplate");
  }, [setWizardStep]);
  const theme = useMantineTheme();
  const reduxDispatch = useAppDispatch();
  const [previewModalOpen, { open: openPreviewModal, close: closePreviewModal }] =
    useDisclosure(false);
  const navigate = useNavigate();
  const countEvent = useCountEvent();

  const createDocument = React.useCallback(() => {
    reduxDispatch(
      librarySlice.actions.newDocumentFromTemplate({
        baseTemplate,
        softwareAddons: selectedAddons,
      })
    );
    countEvent("create-new-document-from-templates-wizard-complete");
    onClose();
    // setSelectedAddons([]);
    navigate("/editor");
  }, [reduxDispatch, baseTemplate, selectedAddons]);
  return (
    <Stack>
      <PreviewModal
        baseTemplate={baseTemplate}
        templates={selectedAddons}
        open={previewModalOpen}
        onClose={closePreviewModal}
      />
      <Group position="right">
        <Text c="dimmed">{selectedAddons.length} selected</Text>
      </Group>
      <Grid>
        {softwareAddonTemplates.map((addon) => {
          const isSelected = !!_.find(selectedAddons, (a) => addon.id === a.id);

          return (
            <Grid.Col lg={3} sm={4} xs={6} key={addon.id}>
              <AddonTemplateCard
                addonTemplate={addon}
                primaryActionLabel={isSelected ? "Remove" : "Select"}
                primaryActionButtonVariant={isSelected ? "outline" : "filled"}
                primaryAction={() => {
                  if (isSelected) {
                    setSelectedAddons(selectedAddons.filter((a) => a.id !== addon.id));
                  } else {
                    setSelectedAddons([...selectedAddons, addon]);
                  }
                }}
                borderColor={isSelected ? theme.colors.blue[5] : undefined}
              />
            </Grid.Col>
          );
        })}
      </Grid>
      <Group position="apart">
        <Button onClick={() => back()} variant="outline" leftIcon={<IconArrowNarrowLeft />}>
          Back
        </Button>
        <Group>
          <Button variant="subtle" onClick={() => openPreviewModal()}>
            Preview
          </Button>
          <Button onClick={() => createDocument()} variant="filled">
            Create Document
          </Button>
        </Group>
      </Group>
    </Stack>
  );
};

const PreviewModal = ({
  baseTemplate,
  templates,
  open,
  onClose,
}: {
  baseTemplate: AircraftAddonTemplate | BlankTemplate;
  templates: SoftwareAddonTemplate[];
  open: boolean;
  onClose: () => void;
}): JSX.Element => {
  const serializedPreviewDocument = newChecklistDocumentFromTemplates(
    "Preview",
    baseTemplate,
    templates
  );
  const previewDocument: ChecklistContents = serializedPreviewDocument.contents;
  const previewStyleSheet = StyleSheet.create(defaultDocumentStyles);

  const [layout, setLayout] = React.useState<DocumentLayout>("oneColumn");

  return (
    <Modal opened={open} title={<Title order={3}>Preview</Title>} onClose={onClose} size="70%">
      <Stack spacing="xs">
        <ScrollArea
          style={{ height: "70vh", border: "1px solid #000" }}
          p="lg"
          styles={(theme) => ({
            root: {
              backgroundColor:
                theme.colorScheme === "dark" ? theme.colors.dark[8] : theme.colors.gray[4],
            },
          })}
        >
          <SoftPDFPreview
            checklistDocumentSnapshot={previewDocument}
            stylesheetSnapshot={previewStyleSheet}
            layout={layout}
          />
        </ScrollArea>
        <Stack align="center">
          <Select
            label="Preview Layout"
            value={layout}
            onChange={(newLayout) =>
              (newLayout === "oneColumn" || newLayout === "twoColumn") && setLayout(newLayout)
            }
            data={documentLayouts.map((layout) => ({
              value: layout,
              label: documentLayoutNames[layout],
            }))}
            style={{ width: "20ch" }}
          />
          <Group position="center">
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </Group>
        </Stack>
      </Stack>
    </Modal>
  );
};
